import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ServiceRouter } from '@pershing/pershing-core';
import { TradingServiceDefinition } from './../../models/common/trading-service-definition';
import { PositonsTradingServiceDefinition } from './../../models/common/positions-service-definition';
import { TradingExchangeOrder, TradeType, TypeAheadSecurityType, RegulatoryInfo, NetxAddDigSig } from '../../models/common/trading-common.model';
import { TradingEntitlements } from '../../models/common/trading-entitlements';
import { reduce, expand } from 'rxjs/operators';
import { EMPTY } from 'rxjs'
import { TradingConstants } from '../../utils/trading-constants';

const httpOptions = {
    headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    })
};

const acctNumParam = 'urn:id:headers.x-AccountNumber';
const ordDltParam = 'urn:id:headers.x-OrdDtlKey';

@Injectable()
export abstract class TradingServiceStrategy {

    private baseUrl: string;
    private positionsUrl:string;
    private platform: string;

    constructor (protected http: HttpClient, protected serviceRouter: ServiceRouter, @Optional() @Inject('AppSettings') appSettings: any) {
        if (appSettings?.platform) {
            this.platform = appSettings.platform;
        } else {
            this.platform = 'professional';
        }
        this.baseUrl = this.serviceRouter.getServiceRouteURL(TradingServiceDefinition.getInstance(this.platform), { ignoreOptions: { serviceVersion: true } });
        this.positionsUrl = this.serviceRouter.getServiceRouteURL(PositonsTradingServiceDefinition.getInstance(this.platform), { ignoreOptions: { serviceVersion: true } });
    }

    private getHttpHeadersWithAcctNum(acctNum: string) {
        return new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'x-AccountNumber': acctNum
        });
    }

    private getHeadersWithAcctNumAndOrdDltKey(acctNum: string,ordDtlKey: string) {
        return new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'x-AccountNumber': acctNum,
            'x-OrdDtlKey': ordDtlKey
        });
    }

    public abstract getAccounts(): Observable<any>;

    public abstract support(platform: string): boolean;

    public abstract getTradingSettings(ibdId: string, assetType: string, role?: string);

    public getBaseUrl(): string {
        return this.baseUrl;
    }

    public getQuote(acctNum: string, symId: string, quoteType: string, selectedSession: string, passComplianceSiteId: boolean): Observable<any> {
        quoteType = quoteType ? quoteType : 'DELAYED';
        let url = this.getBaseUrl() + '/' + acctNumParam + '/' + symId + '/' + 'quote?quoteType=' + quoteType;

        if (selectedSession === 'EXTENDED') {
            url = url + '&isEcn=true';
        }

        if (passComplianceSiteId) {
            url = url + '&passcomplianceSiteId=true';
        }

        return this.http.get(url, { headers: this.getHttpHeadersWithAcctNum(acctNum) });
    }

    public getHoldings(acctNum: string, firmAcct: boolean, assetType: string, includeTradingSpecificData?: boolean): Observable<any> {
        if(firmAcct){
            return this.getFirmAccountHoldings(acctNum);
        }
        let url = this.getBaseUrl()  + '/' + acctNumParam + '/positions';

        if ( !!assetType && includeTradingSpecificData) {
            url += '?assetType=' + assetType +'&includeTradingSpecificData=true';
        } 
        else if ( !!assetType && !includeTradingSpecificData) {
            url += '?assetType=' + assetType;
        }
        else if ( includeTradingSpecificData) {
            url += '?includeTradingSpecificData=true';
        }  

        return this.http.get(url, { headers: this.getHttpHeadersWithAcctNum(acctNum) });
    }

    public getFirmAccountHoldings(acctNum: string): Observable<any> {
        let url = this.positionsUrl  + '/firmaccounts/' + acctNum + '/positions';

       url+="?quoteType=D&isCashIncluded=true&exportAllData=true";

        return this.http.get(url).pipe(
            expand((res:any) => res.response.scrollKey ? this.http.get(url+"&scrollKey="+res.response.scrollKey) : EMPTY),
            reduce((acc:any, res:any) => {
                if(res!==null){
                    acc.errorCode = res.errorCode;
                    acc.errorDescription=res.errorDescription;
                    acc.errorMessage = res.errorMessage;
                    if(!!acc.response){
                        acc.response.positions = acc.response.positions.concat(res.response.positions);
                    }else{
                        acc.response = res.response;    
                    }
                }

                return acc;
            }
                , []));
            
    }

    public getEntitlements(): Observable<any> {
        return this.http.get(this.getBaseUrl() + '/user-trading-role', httpOptions);
    }

    public getAccountDetails(acctNum: string): Observable<any> {
        let url = this.getBaseUrl() + '/' + acctNumParam + '/details';
        return this.http.get(url, { headers: this.getHttpHeadersWithAcctNum(acctNum) });
    }

    public getSymbolQuery(acctNum: string, sym: string, type: TypeAheadSecurityType, excludeNonUSD: boolean): Observable<any> {
        let url = this.getBaseUrl() + '/' + acctNumParam + '/type-ahead/' + type + '/' + sym;

        if ( excludeNonUSD === true ) {
            url = url + '?excludeNonUS=true';
        }

        return this.http.get(url, { headers: this.getHttpHeadersWithAcctNum(acctNum) });
    }

    public getOrderDetails(acctNum: string, ordDtlKey: string, subRequestTypeCode: string, ordProdCodeTex: string): Observable<any> {
        let encodedOrdDtlKey = encodeURIComponent(ordDtlKey);
        let url = this.getBaseUrl() + `/${acctNumParam}/order/${subRequestTypeCode}/${ordProdCodeTex}/detail/${ordDltParam}`;
        if ( ordProdCodeTex.toLowerCase() === TradingConstants.ANNUITY ) {
            ordProdCodeTex = ordProdCodeTex.toLowerCase();
            url = this.getBaseUrl() + `/${acctNumParam}/order/${subRequestTypeCode}/${ordProdCodeTex}-detail/${ordDltParam}`;
        }else if ( ordProdCodeTex.toLowerCase() === TradingConstants.FIXED_INCOME_TYPE ) {           
            url = this.getBaseUrl() + `/${acctNumParam}/order/${subRequestTypeCode}/fi-detail/${ordDltParam}`;
        }else if (ordProdCodeTex.toLowerCase() === TradingConstants.CURRENCY_TYPE || ordProdCodeTex === TradingConstants.INTERNATIONAL_TYPE) {
            ordProdCodeTex = ordProdCodeTex.toLowerCase();
            url = this.getBaseUrl() + `/${acctNumParam}/intlfx-currency-detail/${ordDltParam}`;
        }
        url += this.getTrailingDetails();
        return this.http.get(url, { headers: this.getHeadersWithAcctNumAndOrdDltKey(acctNum, encodedOrdDtlKey) });
    }

    public getEstimatedCommission(order: TradingExchangeOrder): Observable<any> {
        return this.http.post(this.getBaseUrl() + '/commission', order, httpOptions);
    }

    public getApplicableTradeTypes(tradingEntitlements: TradingEntitlements): any {
        let data: TradeType[] = [];

        if (tradingEntitlements.isEquityEnabled) {
            data.push(TradeType.EQUITY);
        }

        if (tradingEntitlements.isMutualFundEnabled) {
            data.push(TradeType.MUTUAL_FUND);
        }

        if (tradingEntitlements.is529FundEnabled) {
            data.push(TradeType.PLAN_529FUND);
        }

        if (tradingEntitlements.isOptionEnabled) {
            data.push(TradeType.OPTION);
        }

        if ( tradingEntitlements.isExtendedHoursEnabled ) {
            data.push(TradeType.EXTENDED_HOURS);
        }

        return data;
    }

    public getTradingDayInfo() {
        return this.http.get(this.getBaseUrl() + '/trading-day-info', httpOptions);
    }

    public saveTradingSettings(ibdId: string, assetType: string, role: string, req: any) {
        return this.http.put(this.getBaseUrl() + `/${ibdId}/${assetType}/save-trade-defaults?role=${role}`, req, httpOptions);
    }

    public getOrderDateTimeStamp(accountNum: string, tranCode: string): Observable<any> {
        let url = this.getBaseUrl() + '/' + acctNumParam + '/' + tranCode + '/ordDateTime';
        return this.http.get(url, { headers: this.getHttpHeadersWithAcctNum(accountNum) });
    }

    public validateOrderDateTimes(regInfo: RegulatoryInfo): Observable<any> {
        return this.http.post(this.getBaseUrl() + '/validate-regulatory-info', regInfo, httpOptions);
    }

    public getNextTradingDate(): Observable<any> {
        return this.http.get(this.getBaseUrl() + '/gtd-dates');
    }

    public getLegalEntity(ibdId: string, accountNum: string): Observable<any> {
        let url = this.getBaseUrl() + `/legalentity/${ibdId}/${acctNumParam}`;
        return this.http.get(url, { headers: this.getHttpHeadersWithAcctNum(accountNum) });
    }

    public getAveragePriceAccountsForUser(): Observable<any> {
        return this.http.get(this.getBaseUrl() + `/average-price-accounts`, httpOptions);
    }

    public updateTermsAndConditionsAcceptance(acceptances: NetxAddDigSig[]): Observable<any> {
        return this.http.post(this.getBaseUrl() + `/update-terms-condition`, acceptances, httpOptions);
    }

    public abstract opraData(NONPROFDATA: any, NASDAQDATA: any, OPRADATA: any, NSPDDATA: any, PROFDATA: any): Observable<any>;

    public getCountriesList(): Observable<any> {
        return this.http.get(this.getBaseUrl() + `/countries`, httpOptions);
    }

    public getCancelAnnuityOrder(acctNum: string, ordDtlKey: string, subRequestTypeCode: string): Observable<any> {
        let encodedOrdDtlKey = encodeURIComponent(ordDtlKey);
        let url = this.getBaseUrl() + `/${acctNumParam}/order/${subRequestTypeCode}/Annuity/cancel/${ordDltParam}`;
        return this.http.delete(url, { headers: this.getHeadersWithAcctNumAndOrdDltKey(acctNum,encodedOrdDtlKey) });
    }

    public abstract getExchangeDetails(symId: string): Observable<any>;

    public abstract getTrailingDetails(): string;
}
